import { useState } from "react";
import { Sparkles, X, Loader2, ArrowRight } from "lucide-react";
import { adminReviseArticle } from "@/lib/blogApi";

const PRESETS = [
  "Make it shorter and more skimmable.",
  "Make it longer and more detailed, around 1500 words.",
  "Add more specific dollar examples (loan amounts, monthly payments).",
  "Make the tone warmer and more empathetic for first-time buyers.",
  "Add a comparison table or numbered checklist where helpful.",
  "Strengthen the closing CTA with a clearer next step.",
];

export default function ReviseArticleModal({ open, articleId, onClose, onComplete }) {
  const [instruction, setInstruction] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const apply = async () => {
    if (!instruction.trim() || working) return;
    setWorking(true);
    setError("");
    try {
      const updated = await adminReviseArticle(articleId, instruction.trim());
      onComplete?.(updated);
      setInstruction("");
    } catch (e) {
      setError(e.message || "Revision failed. Try again.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="revise-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#D41F30] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h2 className="font-display text-xl font-bold text-gray-900">Revise with AI</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"
            data-testid="revise-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Tell the AI what to change. It will rewrite the article using your full current draft as
            context, keeping the CityWorth brand voice intact.
          </p>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="revise-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
              Your revision instruction
            </label>
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={4}
              placeholder='e.g. "Make the intro punchier and add a bullet list comparing FHA vs Conventional in section 2."'
              data-testid="revise-instruction"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
              disabled={working}
            />
          </div>

          <div>
            <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
              Quick presets
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  onClick={() => setInstruction(p)}
                  disabled={working}
                  data-testid={`revise-preset-${p.slice(0, 15).replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            disabled={working}
            className="px-4 py-2.5 text-sm font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={working || !instruction.trim()}
            data-testid="revise-apply"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-full bg-[#D41F30] hover:bg-[#B91C2A] disabled:opacity-60 disabled:cursor-not-allowed text-white shadow"
          >
            {working ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Revising (45s)...
              </>
            ) : (
              <>
                Apply revision <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
